import type { ReactNode } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Spinner
} from 'design-system';

import { useUserDetails } from '@/features/_shared/user';
import useDataHubExport from '@/features/data-hub/hooks/useDataHubExport';
import type { ExportStatus } from '@/features/data-hub/types';

import {
  ExportMetaProvider,
  ExportNameProvider,
  ExportPreviewProvider,
  ExportRequestProvider,
  ExportSettingsProvider,
  useExportRequest
} from './exportContexts';

const PREPARING_STATUSES: ExportStatus[] = ['loading', 'downloading', 'processing'];

export const ExportModalsWrapper = () => {
  const { exportStatus, resetExportStatus } = useExportRequest();

  // Excel, direct downloads and integrations all share the "Preparing your export"
  // dialog; only the Excel flow adds the "we'll send it to your email" note.
  const isPreparing = PREPARING_STATUSES.includes(exportStatus);
  const isEmailedExport = exportStatus === 'loading';
  const isQueued = exportStatus === 'queued';
  const isIntegrationQueued = exportStatus === 'integrationQueued';

  const { userEmail } = useUserDetails();

  return (
    <>
      <Dialog
        disablePointerDismissal
        open={isPreparing}
        onOpenChange={() => {}}
      >
        <DialogContent
          className='sm:max-w-[610px] bg-gx-main-background text-gx-text'
          showCloseButton={false}
        >
          <div className='flex items-center gap-2'>
            <Spinner className='size-6' />
            <DialogTitle className='text-sm leading-[normal] font-bold whitespace-nowrap sm:text-base'>
              Preparing your export
            </DialogTitle>
          </div>
          {isEmailedExport && (
            <DialogDescription className='max-w-[510px] leading-[normal]'>
              This may take a moment. If it takes longer than expected, we'll send the file to your
              email.
            </DialogDescription>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isQueued}
        onOpenChange={nextOpen => {
          if (!nextOpen) resetExportStatus();
        }}
      >
        <DialogContent
          className='sm:max-w-[610px] bg-gx-main-background text-gx-text'
          showCloseButton={false}
        >
          <DialogTitle className='text-sm leading-[normal] font-bold whitespace-nowrap sm:text-base'>
            Your export is on its way
          </DialogTitle>
          <div>
            <DialogDescription className='max-w-[510px] leading-[normal]'>
              The file will be sent to{' '}
              <a
                className='text-inherit'
                href={`mailto:${userEmail}`}
              >
                {userEmail}
              </a>
              .
            </DialogDescription>
            <DialogDescription className='max-w-[510px] leading-[normal]'>
              You can also find it in Past Exports when it's ready.
            </DialogDescription>
          </div>
          <DialogFooter className='mt-0'>
            <Button onClick={resetExportStatus}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isIntegrationQueued}
        onOpenChange={nextOpen => {
          if (!nextOpen) resetExportStatus();
        }}
      >
        <DialogContent
          className='sm:max-w-[610px] bg-gx-main-background text-gx-text'
          showCloseButton={false}
        >
          <DialogTitle>We're on it.</DialogTitle>
          <DialogDescription>
            You will get an email shortly to <span className='underline'>{userEmail}</span>
          </DialogDescription>
          <DialogFooter>
            <Button onClick={resetExportStatus}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const { settings, preview, request, meta, name } = useDataHubExport();

  return (
    <ExportSettingsProvider value={settings}>
      <ExportPreviewProvider value={preview}>
        <ExportRequestProvider value={request}>
          <ExportMetaProvider value={meta}>
            <ExportNameProvider value={name}>
              {children}
              <ExportModalsWrapper />
            </ExportNameProvider>
          </ExportMetaProvider>
        </ExportRequestProvider>
      </ExportPreviewProvider>
    </ExportSettingsProvider>
  );
};
